import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { StoreMainBannerConverter } from '@/converters';
import { StoreMainBannerService } from '../instances/store-main-banner';

// ✅ GET ALL (paginated)
export const useStoreMainBanners = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_STORE_MAIN_BANNER', params],
    async () => {
      const [err, data] = await StoreMainBannerService.getStoreMainBanners(params);
      if (err) throw err;
      return {
        data: StoreMainBannerConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

// ✅ GET BY ID
export const useStoreMainBannerById = () => {
  return useMutation({
    mutationKey: ['GET_STORE_MAIN_BANNER_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await StoreMainBannerService.getStoreMainBannerById(uuid);
      if (err) throw err;
      return StoreMainBannerConverter.toDomainFromByIdResponse(data);
    },
  });
};

// ✅ POST
export const usePostStoreMainBannerMutation = () => {
  return useMutation({
    mutationKey: ['POST_STORE_MAIN_BANNER'],
    mutationFn: async (payload: any) => {
      const [err, data] = await StoreMainBannerService.postStoreMainBanner(payload);
      if (err) throw err;
      return StoreMainBannerConverter.toDomainFromPostResponse(data);
    },
  });
};

// ✅ PUT
export const usePutStoreMainBannerMutation = () => {
  return useMutation({
    mutationKey: ['PUT_STORE_MAIN_BANNER'],
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const [err, data] = await StoreMainBannerService.putStoreMainBanner(id, payload);
      if (err) throw err;
      return StoreMainBannerConverter.toDomainFromPutResponse(data);
    },
  });
};

// ✅ DELETE
export const useDeleteStoreMainBannerMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_STORE_MAIN_BANNER'],
    mutationFn: async (id: string) => {
      const [err] = await StoreMainBannerService.deleteStoreMainBanner(id);
      if (err) throw err;
    },
  });
};
